export interface StrapiAPIResponseArray<T> {
	readonly data: StrapiEntry<T>[];
	readonly meta: Meta;
}

export interface StrapiAPIResponseSingle<T> {
	readonly data: StrapiEntry<T>;
	readonly meta: Meta;
}

export interface StrapiEntry<T> {
	readonly id: number;
	readonly attributes: T;
}

export interface SpeciesAttributes {
	readonly cameraTip: string;
	readonly slug: string;
	readonly titleShort: string;
	readonly titleLong: string;
	readonly level: string;
	readonly createdAt: string;
	readonly updatedAt: string;
	readonly publishedAt: string;
	readonly imageMain: StrapiData<
		StrapiEntry<MediaAttributes> | StrapiEntry<MediaAttributes>[]
	>;
	readonly videoMain: Video;
	readonly speciesKeywords: SpeciesKeywords;
	readonly speciesDescription: SpeciesDescription;
	readonly isRecorderSupported?: boolean;
}
export interface StrapiData<T> {
	readonly data: T | null;
}

export interface MediaEntry {
	readonly data:
	| StrapiEntry<MediaAttributes>
	| StrapiEntry<MediaAttributes>[]
	| null;
}

export interface MediaAttributes {
	readonly name: string;
	readonly alternativeText: string | null;
	readonly caption: null;
	readonly width: number | null;
	readonly height: number | null;
	readonly formats: ImageFormats | null;
	readonly hash: string;
	readonly ext: string;
	readonly mime: string;
	readonly size: number;
	readonly url: string;
	readonly previewURL: null;
	readonly provider: string;
	readonly providerMetadata: null;
	readonly createdAt: string;
	readonly updatedAt: string;
}

export interface ImageFormats {
	readonly thumbnail: ImageFormat;
	readonly small?: ImageFormat;
	readonly medium?: ImageFormat;
}

export interface ImageFormat {
	readonly ext: EXT;
	readonly url: string;
	readonly hash: string;
	readonly mime: MIME;
	readonly name: string;
	readonly path: null;
	readonly size: number;
	readonly width: number;
	readonly height: number;
	readonly sizeInBytes: number;
}

export enum EXT {
	Jpg = '.jpg',
	PNG = '.png',
}

export enum MIME {
	ImageJPEG = 'image/jpeg',
	ImagePNG = 'image/png',
}

export interface SpeciesDescription {
	readonly id: number;
	readonly appearance: AdditionalInformation;
	readonly behavior: AdditionalInformation;
	readonly habitatAndTimeOfYear: AdditionalInformation;
	readonly additionalInformation: AdditionalInformation;
}

export interface AdditionalInformation {
	readonly id: number;
	readonly description: string;
	readonly media: MediaEntry;
}

export interface SpeciesKeywords {
	readonly id: number;
	readonly atStart: SpeciesKeyword[];
	readonly anywhere: SpeciesKeyword[];
	readonly atEnd: SpeciesKeyword[];
}

export interface SpeciesKeyword {
	readonly id: number;
	readonly keyword: string;
	readonly type: string;
}

export interface CalendarAttributes {
	readonly date: string;
	readonly speciesEntry: StrapiData<StrapiEntry<MinimalSpeciesEntry>>;
}

export interface MinimalSpeciesEntry {
	readonly publishedAt: string;
}

export interface CollectionAttributes {
	readonly titleShort: string;
	readonly titleLong: string;
	readonly slug: string;
	readonly speciesEntries: StrapiData<StrapiEntry<MinimalSpeciesEntry>[]>;
}

export interface Video {
	readonly id: number;
	readonly title: string;
	readonly description: string;
	readonly video: StrapiData<StrapiEntry<MediaAttributes>>;
	readonly thumbnail: StrapiData<StrapiEntry<MediaAttributes>>;
	readonly subtitles: StrapiData<StrapiEntry<MediaAttributes>>;
	readonly audioDescription: StrapiData<StrapiEntry<MediaAttributes>>;
}

export interface IntroAttributes {
	readonly video: Video;
}

export interface CollectionCompleteVideoAttributes {
	readonly video: Video;
}

export interface Meta {
	readonly pagination: Pagination;
}

export interface Pagination {
	readonly page: number;
	readonly pageSize: number;
	readonly pageCount: number;
	readonly total: number;
}

export const convertStrapiSingleMediaOrArrayToMediaArray = (
	media: MediaEntry | MediaEntry[],
) => {
	let mediaWrappers: MediaEntry[] = [];
	if (!Array.isArray(media)) {
		mediaWrappers.push(media);
	} else {
		mediaWrappers = media;
	}
	// Get an array of the media item's attributes
	let mediaAttributes: MediaAttributes[] = [];
	mediaWrappers.forEach((wrapper) => {
		// The data can be null
		if (!wrapper.data) return;
		// Or the data can be a single media item
		if (!Array.isArray(wrapper.data)) {
			mediaAttributes.push(wrapper.data.attributes);
		} else {
			// Or the data can be an array of media items
			mediaAttributes = wrapper.data.map((item) => item.attributes);
		}
	});

	return mediaAttributes;
};
